import { setHistoricalSignalSet } from "../Redux/Reducers/strategiesSlice";
import {
  addActiveRequests,
  removeActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../Redux/Reducers/uiSlice";
import { sendWebSocketMessage } from "../hooks/useWebSocket";

export const HISTORICAL_SIGNAL_SET_REQUEST = "get_historical_signal_set";

let inflight = null;

export function getHistoricalSignalInflight() {
  return inflight;
}

export function cancelHistoricalSignalFetch() {
  inflight = null;
}

export function historicalSignalSetsOf(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const raw = payload.signalSets;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") return Object.values(raw);
  return [];
}

export function signalSetMatchesStrategy(signal, strategyId) {
  if (!strategyId) return false;
  const id = signal?.strategy ?? signal?.strategyId;
  if (id == null || id === "") return true;
  return String(id) === String(strategyId);
}

export function historicalSignalSetsForStrategy(payload, strategyId) {
  return historicalSignalSetsOf(payload).filter((signal) =>
    signalSetMatchesStrategy(signal, strategyId)
  );
}

export function historicalCursorOf(payload) {
  const cursor = payload?.cursor || payload?.nextCursor;
  return cursor ? String(cursor) : null;
}

export function hasMoreHistoricalPages(payload) {
  return payload?.hasMore === true || payload?.has_more === true;
}

export function weightEntriesOf(signalSet) {
  if (
    signalSet?.weights &&
    typeof signalSet.weights === "object" &&
    !Array.isArray(signalSet.weights)
  ) {
    return Object.entries(signalSet.weights).map(([symbol, weight]) => ({
      symbol: String(symbol).toUpperCase(),
      weight: Number(weight) || 0,
    }));
  }
  const keys = signalSet?.weights_keys;
  const values = signalSet?.weights_values;
  if (!keys || !values) return [];
  return Object.keys(values)
    .map((key) => {
      const ticker = keys[key];
      if (!ticker?.symbol) return null;
      return {
        symbol: String(ticker.symbol).toUpperCase(),
        weight: Number(values[key]) || 0,
      };
    })
    .filter(Boolean);
}

export function toLegacyWeights(signalSet) {
  if (signalSet?.weights_keys && signalSet?.weights_values) {
    return {
      weights_keys: signalSet.weights_keys,
      weights_values: signalSet.weights_values,
    };
  }
  const weights_keys = {};
  const weights_values = {};
  weightEntriesOf(signalSet).forEach(({ symbol, weight }) => {
    weights_keys[symbol] = { symbol };
    weights_values[symbol] = weight;
  });
  return { weights_keys, weights_values };
}

export function normalizeSignalSet(set, strategy) {
  if (!set || typeof set !== "object") return set;
  const legacy = toLegacyWeights(set);
  return {
    ...set,
    strategy: set.strategy || strategy,
    weights_keys: set.weights_keys || legacy.weights_keys,
    weights_values: set.weights_values || legacy.weights_values,
  };
}

export function mergeHistoricalSignalPage(prev, payload, strategy) {
  const pageSets = historicalSignalSetsOf(payload).map((set) =>
    normalizeSignalSet(set, strategy)
  );
  const prevObj = prev && !Array.isArray(prev) ? prev : {};
  const prevSets = Array.isArray(prevObj.signalSets) ? prevObj.signalSets : [];
  const rest = { ...(payload || {}) };
  delete rest.hasMore;
  delete rest.has_more;
  delete rest.cursor;
  delete rest.nextCursor;
  const hasMore = hasMoreHistoricalPages(payload);
  return {
    ...prevObj,
    ...rest,
    signalSets: [...prevSets, ...pageSets],
    hasMore,
    nextCursor: hasMore ? historicalCursorOf(payload) : null,
    fetchAllSignalsets:
      inflight?.fetchAllSignalsets ?? prevObj.fetchAllSignalsets ?? false,
    fetchPerformance:
      inflight?.fetchPerformance ?? prevObj.fetchPerformance ?? false,
    fetchBacktestSummary:
      inflight?.fetchBacktestSummary ?? prevObj.fetchBacktestSummary ?? false,
  };
}

export function requestHistoricalSignalSet({
  strategy,
  fetchAllSignalsets = false,
  fetchPerformance = false,
  fetchBacktestSummary = false,
  cursor,
  replace = !cursor,
  silent = !!cursor,
  loadingMsg,
} = {}) {
  return (dispatch) => {
    if (!strategy) return;

    inflight = {
      strategy,
      fetchAllSignalsets: !!fetchAllSignalsets,
      fetchPerformance: !!fetchPerformance,
      fetchBacktestSummary: !!fetchBacktestSummary,
      silent: !!silent,
    };

    if (replace) dispatch(setHistoricalSignalSet([]));
    if (!silent) {
      dispatch(addActiveRequests(HISTORICAL_SIGNAL_SET_REQUEST));
      if (loadingMsg) {
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg(loadingMsg));
      }
    }

    const data = {
      strategy,
      fetchPerformance: !!fetchPerformance,
      fetchBacktestSummary: !!fetchBacktestSummary,
      ...(fetchAllSignalsets ? { fetchAllSignalsets: true } : null),
      ...(cursor ? { cursor: String(cursor) } : null),
    };

    sendWebSocketMessage(
      JSON.stringify({
        action: HISTORICAL_SIGNAL_SET_REQUEST,
        data,
      })
    );
  };
}

export function applyHistoricalSignalSetMessage(dispatch, getState, data) {
  const payload = data?.data && typeof data.data === "object" ? data.data : data;
  const prev = getState().strategies.historicalSignalSet;
  const strategy = inflight?.strategy;
  dispatch(
    setHistoricalSignalSet(mergeHistoricalSignalPage(prev, payload, strategy))
  );
  if (!inflight?.silent) {
    dispatch(removeActiveRequests(HISTORICAL_SIGNAL_SET_REQUEST));
    dispatch(updateLoading(false));
  }
  inflight = null;
}
